import type { ArtistSummary, Breakdown } from '@dashboard/contract';

export function MethodNotes({
  summary,
  breakdown,
  notes,
}: {
  summary: ArtistSummary;
  breakdown: Breakdown | undefined;
  notes: string[];
}) {
  return (
    <details className="method">
      <summary>Method and caveats</summary>
      <div className="notes">
        <div className="note">
          <h3>Measurement basis</h3>
          <p>
            <b>Track-level [T].</b> Every figure is summed from the per-track daily series in the
            Ghost streaming database — the ingested recordings per artist. <em>Total Artist</em>{' '}
            therefore means the total of those tracks, not the artist's true worldwide total; the
            hero shows how much of the artist-level figure they cover.
          </p>
          <p>
            Worldwide is Luminate <code>AA</code>. Ex-US is <code>AA − US</code>, differenced period
            by period and floored at zero.
          </p>
        </div>
        <div className="note">
          <h3>Periods</h3>
          <p>
            Calendar years run 1 Jan–31 Dec. Both YTD columns run 1 Jan to the same day of the year,
            so the YTD comparison is like-for-like. LTM is the 12 months ending on the anchor (
            {summary.anchor}); LTM-1 the 12 before that. The anchor is the newest complete worldwide
            day held for the artist.
          </p>
          {breakdown ? (
            <p>
              Total basis <code>{breakdown.basisWindow.label}</code> ranks the Top N and sets every
              % Total, measured over{' '}
              <code>
                {breakdown.basisWindow.start} → {breakdown.basisWindow.end}
              </code>
              .
            </p>
          ) : null}
        </div>
        <div className="note warn">
          <h3>Why growth starts at 2022</h3>
          <p>
            Luminate's coverage steps down sharply in 2020 — US at January, worldwide at April — so
            CY2020 reads high for every artist. It is excluded entirely, which also removes the 2021
            YoY that would have been measured against it. The first sound comparison is{' '}
            <b>2022 against 2021</b>, and the CAGR compounds from <b>2021</b>.
          </p>
        </div>
        <div className="note">
          <h3>Release dates</h3>
          <p>
            Luminate stores an ISRC-registration date, not the commercial release date, which can
            mis-assign vintage by years. Researched commercial dates override it where the team has
            verified them, marked <span className="ovr">◆</span>.
          </p>
          <p>
            <b>{summary.artistName}:</b>{' '}
            {summary.overrideCount
              ? `${summary.overrideCount} of ${summary.trackCount} recordings carry a verified override.`
              : 'no overrides researched yet — vintages are raw Luminate dates, so the catalogue split is indicative only.'}
            {breakdown?.notes.unknownReleaseYear
              ? ` ${breakdown.notes.unknownReleaseYear} recordings have no release date; they are neither catalogue nor new and sit in their own "Unknown Release" row until the date is researched.`
              : ''}
          </p>
        </div>
        <div className="note warn">
          <h3>Geography is a different basis</h3>
          <p>
            The market split is <b>artist-level [A]</b>: Luminate's whole-catalogue totals per
            market over the LTM window. It does not follow the Total basis selector. Outside US and
            Canada, Luminate delivers premium streams only, so those slices are indicative.
          </p>
        </div>
        <div className="note">
          <h3>Events</h3>
          <p>
            A month is an event for a track when it beats its own six-month median by 50% or more
            and gains at least 3M streams. New releases are excluded. <b>Kept</b> is how much of the
            lift survived six months on. Causes come from the team's known events; an analyst can
            link one from the candidates.
          </p>
        </div>
        <div className="note">
          <h3>Getting the numbers out</h3>
          <p>
            <b>Copy</b> buttons put tab-separated text on the clipboard; <b>Download</b> saves the
            same as a file. The daily matrix is one row per day from 2019-01-08: the top N songs as
            columns in the chart's order, then Other (catalogue), Other (new releases), Total and
            the two total splits. Worldwide.
          </p>
        </div>
        {notes.length ? (
          <div className="note">
            <h3>Server notes</h3>
            {notes.map((n) => (
              <p key={n}>{n}</p>
            ))}
          </div>
        ) : null}
      </div>
    </details>
  );
}
